import { useState } from "react";
import { Rectangle, useMapEvents } from "react-leaflet";
import { latLngBounds, type LatLng, type LatLngBounds } from "leaflet";

export type RectangleSelectionMode = "add" | "subtract";

const ADD_STYLE = { color: "#e0913f", weight: 1, dashArray: "4", fillOpacity: 0.1 };
const SUBTRACT_STYLE = { color: "#c0392b", weight: 1, dashArray: "4", fillOpacity: 0.1 };

// Below this many screen pixels of movement between mousedown and mouseup,
// treat it as a click, not a drag — Shift/Ctrl + a plain click (no real
// drag) is handled entirely by the clicked feature's own "click" listener
// (SubBasinMap.tsx/AssignmentMap.tsx, shift adds/ctrl removes that one
// sub-basin), which fires independently of this component. Without this
// threshold, a Shift/Ctrl+click on a sub-basin used to ALSO fire here with
// a degenerate zero-size bounds — selecting/deselecting by bounding-box
// overlap (which can match more than the one sub-basin actually clicked,
// for adjacent/overlapping bboxes) at the same time the click handler was
// already toggling that same one, fighting each other.
const DRAG_THRESHOLD_PX = 4;

/**
 * Selection rectangle: Shift + drag ADDS whatever it touches to the
 * selection, Ctrl + drag SUBTRACTS it — never toggles (before, Shift used
 * to toggle each sub-basin touched, and it was easy to accidentally
 * deselect something the rectangle merely grazed without meaning to). The
 * rectangle's color while dragging shows which mode is active. Extracted
 * from AssignmentMap.tsx to reuse it in the main map too (SubBasinMap.tsx)
 * in edit mode — same mechanism, two maps.
 */
export function RectangleSelectionLayer({
  onSelect,
}: {
  onSelect: (bounds: LatLngBounds, mode: RectangleSelectionMode) => void;
}) {
  const [start, setStart] = useState<LatLng | null>(null);
  const [current, setCurrent] = useState<LatLng | null>(null);
  const [mode, setMode] = useState<RectangleSelectionMode>("add");
  const [isDragging, setIsDragging] = useState(false);

  const map = useMapEvents({
    mousedown(e) {
      if (e.originalEvent.shiftKey) setMode("add");
      else if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) setMode("subtract");
      else return;
      map.dragging.disable();
      setStart(e.latlng);
      setCurrent(e.latlng);
      setIsDragging(false);
    },
    mousemove(e) {
      if (!start) return;
      setCurrent(e.latlng);
      if (!isDragging) {
        const distance = map
          .latLngToContainerPoint(start)
          .distanceTo(map.latLngToContainerPoint(e.latlng));
        if (distance > DRAG_THRESHOLD_PX) setIsDragging(true);
      }
    },
    mouseup(e) {
      if (!start) return;
      if (isDragging) onSelect(latLngBounds(start, e.latlng), mode);
      setStart(null);
      setCurrent(null);
      setIsDragging(false);
      map.dragging.enable();
    },
  });

  if (!start || !current || !isDragging) return null;
  return (
    <Rectangle
      bounds={latLngBounds(start, current)}
      pathOptions={mode === "add" ? ADD_STYLE : SUBTRACT_STYLE}
    />
  );
}
